/* =====================================================================
 * Estimate(견적) Agent API client — `/api/v1/estimate/*` 의 thin wrapper.
 *
 * 프로젝트 / 폴더 / 파일 영속화 엔드포인트를 React Query 가 쉽게 쓸 수
 * 있도록 함수 단위로 노출한다. 파일 자체는 `workspace_files` 테이블을
 * 재사용하므로 응답 모양은 그대로 `WorkspaceFile` 이다 — 파일 저장소
 * 페이지의 동기화를 자연스럽게 한다.
 *
 * 멀티테넌트
 *   견적 에이전트는 b2c + samsung 공통 기능이며, 백엔드 store 가 PG/MSSQL
 *   양쪽을 지원한다. 본 클라이언트는 `/api/v1/estimate/*` 만 호출한다
 *   (CAD `/api/v1/cad/*` 의존성 없음).
 * ===================================================================== */

import type {
  ActiveFileRef,
  AgentStreamResponse,
  ChatHistoryMessage,
  WorkspaceFile,
} from '@auw/workspace-utils';
import {
  authFetch,
  getHeaders as baseGetHeaders,
  ensureFreshToken,
  API_BASE_URL,
} from './authFetch';

const ESTIMATE_PREFIX = `${API_BASE_URL}/api/v1/estimate`;

/** 견적 에이전트 채팅이 프록시되는 워크스페이스 타입. backend
 * `SUPPORTED_WORKSPACES` 와 AI `WORKSPACE_REGISTRY` 의 키와 일치한다. */
const ESTIMATE_WORKSPACE_TYPE = 'estimate-agent';

function jsonHeaders(): Record<string, string> {
  return baseGetHeaders(true);
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface EstimateProject {
  project_id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface EstimateFolder {
  folder_id: string;
  project_id: string;
  parent_folder_id: string | null;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface EstimateUploadResponse {
  uploaded: WorkspaceFile[];
  count: number;
  errors: Array<{ file: string; error: string }> | null;
}

/* ------------------------------------------------------------------ */
/* Projects                                                            */
/* ------------------------------------------------------------------ */

export async function listEstimateProjects(): Promise<EstimateProject[]> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/projects`, {
    headers: jsonHeaders(),
  });
  const data = await unwrap<{ projects: EstimateProject[]; count: number }>(res);
  return data.projects;
}

export async function createEstimateProject(name: string): Promise<EstimateProject> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/projects`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  return unwrap<EstimateProject>(res);
}

export async function renameEstimateProject(
  projectId: string,
  name: string,
): Promise<EstimateProject> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/projects/${projectId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  return unwrap<EstimateProject>(res);
}

export async function deleteEstimateProject(projectId: string): Promise<void> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/projects/${projectId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ status: string; project_id: string }>(res);
}

/* ------------------------------------------------------------------ */
/* Folders                                                             */
/* ------------------------------------------------------------------ */

export async function listEstimateFolders(
  projectId: string,
): Promise<EstimateFolder[]> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/projects/${projectId}/folders`, {
    headers: jsonHeaders(),
  });
  const data = await unwrap<{ folders: EstimateFolder[]; count: number }>(res);
  return data.folders;
}

export async function createEstimateFolder(
  projectId: string,
  name: string,
  parentFolderId: string | null = null,
): Promise<EstimateFolder> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/projects/${projectId}/folders`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      name,
      parent_folder_id: parentFolderId,
    }),
  });
  return unwrap<EstimateFolder>(res);
}

export async function renameEstimateFolder(
  folderId: string,
  name: string,
): Promise<EstimateFolder> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/folders/${folderId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  return unwrap<EstimateFolder>(res);
}

export async function deleteEstimateFolder(folderId: string): Promise<void> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/folders/${folderId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ status: string; folder_id: string }>(res);
}

/* ------------------------------------------------------------------ */
/* Files                                                               */
/* ------------------------------------------------------------------ */

export interface EstimateFileFilters {
  /** 점 유무 무관, 대소문자 무관. 빈 배열/undefined = 필터 없음. */
  readonly extensions?: readonly string[];
}

function appendCsv(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | undefined,
): void {
  if (!values || values.length === 0) return;
  const joined = values.map((v) => v.trim()).filter(Boolean).join(',');
  if (joined) params.set(key, joined);
}

export async function listEstimateFiles(
  projectId: string,
  folderId: string | null,
  filters?: EstimateFileFilters,
  opts?: { all?: boolean },
): Promise<WorkspaceFile[]> {
  const params = new URLSearchParams();
  // all=true 면 폴더 깊이와 무관하게 프로젝트 전체 파일을 가져온다(트리 hydrate).
  // 깊은 폴더에 올린 파일이 새로고침/재방문 후에도 트리에 유지되도록 한다.
  if (opts?.all) {
    params.set('all', 'true');
  } else if (folderId) {
    params.set('folder_id', folderId);
  }
  if (filters) {
    appendCsv(params, 'extensions', filters.extensions);
  }
  const qs = params.toString();
  const res = await authFetch(
    `${ESTIMATE_PREFIX}/projects/${projectId}/files${qs ? `?${qs}` : ''}`,
    { headers: jsonHeaders() },
  );
  const data = await unwrap<{ files: WorkspaceFile[]; count: number }>(res);
  return data.files;
}

/** 서버에 영속된 파일 썸네일(WebP)을 file_id 로 받아 dataURL 로 반환.
 *  업로드한 브라우저뿐 아니라 어떤 기기/브라우저에서도 동일 썸네일을 받는다
 *  (서버 영속). 없거나 비대상(이미지·PDF 아님)이면 null → 호출부는 아이콘 폴백. */
export async function fetchEstimateThumbnail(fileId: string): Promise<string | null> {
  try {
    const res = await authFetch(`${ESTIMATE_PREFIX}/files/${fileId}/thumbnail`, {
      headers: { Accept: 'image/webp,image/*' },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

export async function uploadEstimateFiles(
  projectId: string,
  files: ReadonlyArray<{ blob: Blob; name: string }>,
  folderId: string | null,
): Promise<EstimateUploadResponse> {
  const form = new FormData();
  for (const f of files) {
    form.append('files', f.blob, f.name);
  }
  if (folderId) form.append('folder_id', folderId);

  const res = await authFetch(`${ESTIMATE_PREFIX}/projects/${projectId}/files`, {
    method: 'POST',
    body: form,
  });
  return unwrap<EstimateUploadResponse>(res);
}

export async function moveEstimateFile(
  fileId: string,
  folderId: string | null,
): Promise<WorkspaceFile> {
  const res = await authFetch(`${ESTIMATE_PREFIX}/files/${fileId}/move`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ folder_id: folderId }),
  });
  return unwrap<WorkspaceFile>(res);
}

/* ------------------------------------------------------------------ */
/* Chat (streaming)                                                    */
/* ------------------------------------------------------------------ */

/**
 * 견적 에이전트 채팅 요청. 통합 에이전트(`AgentRequest`)의 부분집합으로,
 * 견적 상세 페이지가 좌측 파일을 컨텍스트로 넘기고 모드(auto/fast/plan/skill)
 * 를 선택할 수 있게 한다. `workspace_type` 은 항상 `estimate-agent` 로
 * 고정되므로 본 인터페이스에는 노출하지 않는다.
 */
export interface EstimateChatRequest {
  query: string;
  /** 파일별 영속 세션 키 — 같은 파일이면 서버 세션 메모리가 유지된다. */
  session_id?: string | null;
  /** 권한/참조 범위 판단용 현재 아이템 ID(있으면). */
  item_id?: string | null;
  message_id?: string | null;
  /** 좌측에서 보고 있는 견적 파일을 컨텍스트로 핀(file_id 중심 계약). */
  active_file_refs?: ActiveFileRef[] | null;
  /** auto → null(RootAgent 판단), 그 외는 강제 모드. */
  force_mode?: 'fast' | 'plan' | 'skill' | null;
  /** skill 모드에서 활성화할 사용자 스킬 ID 목록. */
  active_skill_ids?: string[] | null;
  /** 멀티턴 컨텍스트(이전 대화). */
  history?: ChatHistoryMessage[] | null;
}

/**
 * 견적 에이전트 워크스페이스(`/workspaces/estimate-agent/run/stream`)로
 * SSE 스트리밍 요청을 보내고 `AgentStreamResponse` 프레임을 yield 한다.
 *
 * 통합 에이전트의 `streamAgent` 와 **동일한 SSE 파싱 루프**를 사용하지만
 * 견적 도메인 전용으로 분리해(`workspace_type` 고정), 통합 채팅 스택에
 * 런타임 의존하지 않는다 — CAD `streamCadMaterialChat` 와 같은 패턴.
 * 견적 AI 그래프가 통합 4-모드 라우터의 구조적 클론이므로 프레임 스키마는
 * 통합과 byte 동일하다.
 */
export async function* streamEstimateChat(
  request: EstimateChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<AgentStreamResponse> {
  // 만료 직전 토큰으로 SSE 진입 시의 refresh 경합 방지(streamAgent 와 동일).
  await ensureFreshToken();

  const url = `${API_BASE_URL}/api/v1/workspaces/${ESTIMATE_WORKSPACE_TYPE}/run/stream`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: {
      ...baseGetHeaders(true),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      ...request,
      workspace_type: ESTIMATE_WORKSPACE_TYPE,
      stream: true,
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      } else if (line.trim() === '' && dataLines.length > 0) {
        const raw = dataLines.splice(0).join('\n').trim();
        if (!raw) continue;
        try {
          const parsed: AgentStreamResponse = JSON.parse(raw);
          yield parsed;
          if (parsed.status === 'completed' || parsed.status === 'failed') return;
        } catch {
          console.warn('[Estimate SSE] JSON parse error', raw.slice(0, 200));
        }
      }
    }

    if (dataLines.length > 0) {
      const raw = dataLines.splice(0).join('\n').trim();
      if (raw) {
        try {
          const parsed: AgentStreamResponse = JSON.parse(raw);
          yield parsed;
          if (parsed.status === 'completed' || parsed.status === 'failed') return;
        } catch {
          buffer = `data:${raw}\n${buffer}`;
        }
      }
    }
  }
}
